import { handleSearchURL, getValidHash } from "./helpers/URLHandler.js";
import { createRouter, createWebHistory } from "vue-router";
import * as helpers from "@/helpers/Router";
import { reducedMotionRequested } from "@thegetty-private/getty-ui";

export default createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  //defining the scrolling behavior when navigating using Vue router
  //for more options check: https://router.vuejs.org/guide/advanced/scroll-behavior.html
  scrollBehavior(to, from, savedPosition) {
    // saved position is when the user hits 'back'
    if (savedPosition) {
      // we use a promise/timeout to allow Vue to render the page enough to scroll down
      return new Promise((resolve, _reject) => {
        setTimeout(() => {
          resolve(savedPosition);
        }, 500);
      });
    }

    if (to.hash && getValidHash(to.hash) != undefined) {
      return {
        selector: getValidHash(to.hash),
        behavior: reducedMotionRequested() ? "auto" : "smooth"
      };
    }

    // don't scroll if navigating within the same page (object tabs, object canvases)
    if (from.path == to.path) {
      return;
    }

    // if we're playing with the search facets, don't change where we are on the page
    if (
      to.name == "search" &&
      from.name == "search" &&
      to?.state?.scrollToTop == undefined
    ) {
      return;
    }
    //DEFAULT: scroll to the top of the page unless the URL contains a tab parameter
    return { left: 0, top: 0 };
  },
  routes: [
    {
      path: "/",
      component: () => import("@/views/layout-base.vue"),
      children: [
        {
          path: "/",
          name: "home",
          component: () => import("@/views/home-layout.vue")
        },
        {
          path: "/exhibition",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/views/with-object-page-layout.vue"),
          children: [
            {
              path: ":slug",
              name: "exhibition",
              component: () => import("@/components/pages/exhibition/index.vue")
            }
          ]
        },
        {
          path: "/past-exhibitions",
          name: "pastExhibitions",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () =>
            import("@/components/pages/pastExhibitions/index.vue"),
          beforeEnter: (to, _from, next) => {
            next();
          }
        },
        {
          path: "/galleries",
          name: "galleries",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/components/pages/galleryList/index.vue"),
          beforeEnter: (to, _from, next) => {
            next();
          }
        },
        {
          path: "/imageRequest",
          name: "imageRequest",
          beforeEnter: (to, _from, next) => {
            // I don't quite understand why this is needed here for this to work, but it somehow is
            if (window.history.state.completedEula) {
              to.state = { completedEula: window.history.state.completedEula };
            }
            next();
          },
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/views/image-request.vue")
        },
        {
          path: "/legal",
          name: "legal",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/components/pages/legal/index.vue")
        },
        {
          path: "/thankYou",
          name: "thank-you",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/components/pages/thankYou/index.vue")
        },
        {
          path: "/search",
          name: "search",
          props: true,
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/views/search.vue"),
          beforeEnter: async (to, from, next) => {
            try {
              await handleSearchURL(to, next);
            } catch (e) {
              console.error("URL Preparation Failed: ", e);
              next(from);
            }
          }
        },
        {
          path: "/object",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/views/with-object-page-layout.vue"),
          beforeEnter: (to, _from, next) => {
            helpers.checkForAndHandleTmsDorRedirect(to, _from, next);
          },
          children: [
            {
              path: ":slug",
              name: "object",
              component: () =>
                import("@/components/pages/humanMadeObject/index.vue"),
              beforeEnter: (to, _from, next) => {
                next();
              }
            }
          ]
        },
        {
          path: "/group",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/views/with-object-page-layout.vue"),
          props: true,
          beforeEnter: (to, _from, next) => {
            helpers.checkForAndHandleTmsDorRedirect(to, _from, next);
          },
          children: [
            {
              path: ":slug",
              name: "group",
              component: () => import("@/components/pages/actor/index.vue"),
              beforeEnter: (to, _from, next) => {
                next();
              }
            }
          ]
        },
        {
          path: "/person",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/views/with-object-page-layout.vue"),
          props: true,
          beforeEnter: (to, _from, next) => {
            helpers.checkForAndHandleTmsDorRedirect(to, _from, next);
          },
          children: [
            {
              path: ":slug",
              name: "person",
              component: () => import("@/components/pages/actor/index.vue"),
              beforeEnter: (to, _from, next) => {
                next();
              }
            }
          ]
        },
        {
          path: "/gallery",
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import("@/views/with-object-page-layout.vue"),
          children: [
            {
              path: ":slug",
              name: "gallery",
              component: () => import("@/components/pages/gallery/index.vue")
            }
          ]
        }
      ]
    },
    {
      // will match everything
      path: "/:pathMatch(.*)*",
      component: () => import("@/views/layout-base.vue"),
      children: [
        {
          path: "",
          name: "error",
          component: () => import("@/components/pages/error/index.vue")
        }
      ]
    }
  ]
});
